import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle, CalendarCheck, CheckCircle2, ChevronLeft, Clock, Fingerprint,
  Loader2, Save, User,
} from "lucide-react";
import {
  useHrGetStaffMemberQuery,
  useHrUpdateStaffFieldsMutation,
  useHrGetAttendanceQuery,
} from "../api/hrApi";
import { attendanceColor, errMsg, fmtDate, fmtMinutes, fmtTime, monthRange, staffName, timeInput } from "../utils/hr";

/**
 * One staff member's HR record: the two fields HR owns (biometric id + work
 * window), their leave entitlements, and this month's attendance.
 */
export default function HrStaffDetailPage() {
  const { id = "" } = useParams();

  const { data: staff, isLoading, isError } = useHrGetStaffMemberQuery(id, { skip: !id });
  const [save, { isLoading: saving }] = useHrUpdateStaffFieldsMutation();

  const { from, to } = monthRange();
  const { data: attendance } = useHrGetAttendanceQuery({ admin_id: id, from, to }, { skip: !id });

  const [form, setForm] = useState({ biometric_id: "", work_start: "", work_end: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!staff) return;
    setForm({
      biometric_id: staff.biometric_id ?? "",
      work_start: timeInput(staff.work_start),
      work_end: timeInput(staff.work_end),
    });
  }, [staff]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await save({
        id,
        body: {
          biometric_id: form.biometric_id.trim() || null,
          work_start: form.work_start || null,
          work_end: form.work_end || null,
        },
      }).unwrap();
      setMsg({ ok: true, text: "Staff HR details updated." });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ ok: false, text: errMsg(err) });
    }
  };

  const balances: any[] = staff?.leave_balances ?? staff?.leaveBalances ?? [];
  const days: any[] = attendance?.rows ?? [];

  const inputCls =
    "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
  const labelCls = "block text-xs font-medium text-gray-600 mb-1";

  if (isLoading) {
    return (
      <div className="py-24 flex justify-center text-gray-300">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (isError || !staff) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white border rounded-xl p-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-600">Could not load this staff record.</p>
          <Link to="/hr/staff" className="inline-block mt-4 px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Back to staff
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <Link to="/hr/staff" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ChevronLeft className="w-4 h-4" /> All staff
      </Link>

      {/* Identity */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <User className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900">{staffName(staff)}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {[staff.job_title, staff.department?.name, staff.designation?.name].filter(Boolean).join(" · ") || "—"}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
              {staff.staff_no && <span>Staff No: <span className="text-gray-800 font-medium">{staff.staff_no}</span></span>}
              {staff.user?.email && <span>{staff.user.email}</span>}
              {staff.status && <span>Status: <span className="text-gray-800 font-medium">{staff.status}</span></span>}
            </div>
          </div>
        </div>
      </div>

      {/* HR fields */}
      <form onSubmit={submit} className="bg-white border rounded-xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Fingerprint className="w-4 h-4 text-indigo-600" />
          <h2 className="font-semibold text-gray-900">HR settings</h2>
        </div>
        <p className="text-xs text-gray-500 mb-5">
          The biometric id matches this person to rows in the device export. The work window is what lateness and
          hours worked are measured against. Everything else about them is edited under{" "}
          <Link to={`/admins/${staff.id}`} className="text-indigo-600 hover:underline">Admins</Link>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1">
            <label className={labelCls}>Biometric ID</label>
            <input
              value={form.biometric_id}
              onChange={(e) => setForm((f) => ({ ...f, biometric_id: e.target.value }))}
              placeholder="e.g. 1042"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Work starts</label>
            <input
              type="time"
              value={form.work_start}
              onChange={(e) => setForm((f) => ({ ...f, work_start: e.target.value }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Work ends</label>
            <input
              type="time"
              value={form.work_end}
              onChange={(e) => setForm((f) => ({ ...f, work_end: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {msg && (
          <div
            className={`mt-4 flex items-center gap-2 px-3 py-2 rounded-lg text-xs border ${
              msg.ok
                ? "bg-green-50 border-green-100 text-green-700"
                : "bg-red-50 border-red-100 text-red-700"
            }`}
          >
            {msg.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {msg.text}
          </div>
        )}

        <div className="mt-5">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save HR settings
          </button>
        </div>
      </form>

      {/* Leave balances */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-indigo-600" />
            <h2 className="font-semibold text-gray-900">Leave entitlements</h2>
          </div>
          <Link to="/hr/leave" className="text-xs text-indigo-600 hover:underline">Manage leave</Link>
        </div>

        {balances.length === 0 ? (
          <p className="text-sm text-gray-500">No entitlements allocated yet.</p>
        ) : (
          <div className="border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                  <th className="px-4 py-2 font-medium">Leave type</th>
                  <th className="px-4 py-2 font-medium">Year</th>
                  <th className="px-4 py-2 font-medium text-right">Entitled</th>
                  <th className="px-4 py-2 font-medium text-right">Used</th>
                  <th className="px-4 py-2 font-medium text-right">Remaining</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {balances.map((b: any) => {
                  const entitled = Number(b.entitled_days ?? 0);
                  const used = Number(b.used_days ?? 0);
                  return (
                    <tr key={b.id}>
                      <td className="px-4 py-2.5 text-gray-800">{b.leave_type?.name ?? b.leaveType?.name ?? "—"}</td>
                      <td className="px-4 py-2.5 text-gray-600">{b.year}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{entitled}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{used}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">
                        {b.remaining_days ?? entitled - used}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* This month's attendance */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-600" />
            <h2 className="font-semibold text-gray-900">Attendance — {fmtDate(from)} to {fmtDate(to)}</h2>
          </div>
          <Link to="/hr/attendance" className="text-xs text-indigo-600 hover:underline">Full register</Link>
        </div>

        {days.length === 0 ? (
          <p className="text-sm text-gray-500">No attendance recorded this month.</p>
        ) : (
          <div className="border rounded-xl overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="text-left text-gray-500 border-b bg-gray-50">
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">In</th>
                  <th className="px-4 py-2 font-medium">Out</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Late</th>
                  <th className="px-4 py-2 font-medium text-right">Worked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {days.map((d: any) => (
                  <tr key={d.id}>
                    <td className="px-4 py-2.5 text-gray-800 whitespace-nowrap">{fmtDate(d.work_date)}</td>
                    <td className="px-4 py-2.5 text-gray-600">{fmtTime(d.clock_in)}</td>
                    <td className="px-4 py-2.5 text-gray-600">{fmtTime(d.clock_out)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${attendanceColor(d.status)}`}>
                        {d.status ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmtMinutes(d.minutes_late)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">{fmtMinutes(d.minutes_worked)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
